import React, { useState } from "react";
import { Alert, AlertTitle, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { authorizeProject, rejectProject } from "../redux/ducks/project";
import { getProject } from "../redux/slices/project";
import { useIsProjectLocked } from "../utils/lockUtils";

const NoticeAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    alignItems: "center",
}));

const NoticeActions = styled.div(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
}));

const DIALOGS = {
    authorize: {
        title: "Authorize this project?",
        helper: (name) => `You are confirming that the work on "${name}" can go ahead. The project will move to Pending.`,
        action: "Authorize project",
        busy: "Authorizing…",
        color: "primary",
        creator: authorizeProject,
    },
    reject: {
        title: "Reject this project?",
        helper: (name) => `Rejecting "${name}" cancels the project.`,
        action: "Reject project",
        busy: "Rejecting…",
        color: "error",
        creator: rejectProject,
    },
};

const ProjectAuthorizationNotice = () => {
    const dispatch = useDispatch();
    const project = useSelector((state) => state.project.currentProject);
    const [dialogKey, setDialogKey] = useState("authorize");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isLocked = useIsProjectLocked(project);

    if (project?.status !== "Awaiting Authorization" || !project?.canAuthorize || isLocked) return null;

    const dialog = DIALOGS[dialogKey];

    const handleOpen = (key) => {
        setDialogKey(key);
        setDialogOpen(true);
    };

    const handleClose = () => {
        if (!isSubmitting) setDialogOpen(false);
    };

    const handleConfirm = () => {
        setIsSubmitting(true);
        dispatch(
            dialog.creator(project._id, (response) => {
                setIsSubmitting(false);
                setDialogOpen(false);
                if (!response) dispatch(getProject({ projectId: project._id }));
            }),
        );
    };

    return (
        <>
            <NoticeAlert
                severity="warning"
                action={
                    <NoticeActions>
                        <Button variant="outlined" color="error" onClick={() => handleOpen("reject")}>
                            Reject
                        </Button>
                        <Button variant="contained" color="warning" onClick={() => handleOpen("authorize")}>
                            Authorize
                        </Button>
                    </NoticeActions>
                }
            >
                <AlertTitle>Awaiting authorization</AlertTitle>
            </NoticeAlert>

            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="xs">
                <DialogTitle>{dialog.title}</DialogTitle>
                <DialogContent>
                    <Typography>{dialog.helper(project.name)}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={dialog.color}
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isSubmitting ? dialog.busy : dialog.action}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProjectAuthorizationNotice;
